import React from "react"

interface PaginationProps {
  pagination: PaginationInfo | undefined
  onPageChange: (page: number) => void
}

/** ページネーション情報 */
export interface PaginationInfo {
  currentPage: number
  totalCount: number
  totalPages: number
}

const Pagination: React.FC<PaginationProps> = ({ pagination, onPageChange }) => {
  if (!pagination || pagination.totalPages <= 1) {
    return null
  }

  const { currentPage, totalPages } = pagination

  // ページ範囲を計算する関数
  const getPageRange = (): (number | "ellipsis")[] => {
    const range: (number | "ellipsis")[] = []
    const showEllipsis = totalPages > 7

    if (!showEllipsis) {
      // 総ページ数が7以下の場合、全てのページを表示
      for (let i = 1; i <= totalPages; i++) {
        range.push(i)
      }
    } else {
      // 総ページ数が多い場合の表示ロジック
      range.push(1)

      if (currentPage > 4) {
        range.push("ellipsis")
      }

      // 現在のページ周辺のページを表示
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        if (!range.includes(i)) {
          range.push(i)
        }
      }

      if (currentPage < totalPages - 3) {
        range.push("ellipsis")
      }

      if (totalPages > 1) {
        range.push(totalPages)
      }
    }

    return range
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const handlePageClick = (page: number) => {
    onPageChange(page)
  }

  const pageRange = getPageRange()

  return (
    <div className="mt-6 flex items-center justify-center gap-3">
      {/* 前のページボタン */}
      <button
        onClick={handlePrevPage}
        disabled={currentPage <= 1}
        className={`flex h-12 w-12 items-center justify-center border border-black ${
          currentPage <= 1
            ? "cursor-not-allowed bg-gray-100 text-gray-400"
            : "bg-white hover:bg-gray-50"
        }`}
      >
        <svg className="h-3 w-2" fill="currentColor" viewBox="0 0 8 12">
          <path d="M7 12L1 6l6-6v12z" />
        </svg>
      </button>

      {/* ページ番号 */}
      {pageRange.map((page, index) => {
        if (page === "ellipsis") {
          return (
            <span key={`ellipsis-${index}`} className="px-2 text-[1.6rem] text-black">
              ・・・
            </span>
          )
        }

        const isCurrentPage = page === currentPage

        return (
          <button
            key={page}
            onClick={() => handlePageClick(page)}
            className={`flex h-12 w-12 items-center justify-center border border-black text-[1.4rem] ${
              isCurrentPage ? "bg-black text-white" : "bg-white text-black hover:bg-gray-50"
            }`}
          >
            {page}
          </button>
        )
      })}

      {/* 次のページボタン */}
      <button
        onClick={handleNextPage}
        disabled={currentPage >= totalPages}
        className={`flex h-12 w-12 items-center justify-center border border-black ${
          currentPage >= totalPages
            ? "cursor-not-allowed bg-gray-100 text-gray-400"
            : "bg-white hover:bg-gray-50"
        }`}
      >
        <svg className="h-3 w-2" fill="currentColor" viewBox="0 0 8 12">
          <path d="M1 0l6 6-6 6V0z" />
        </svg>
      </button>
    </div>
  )
}

export default Pagination
